/**
 * Trip summary and driving-style breakdown.
 *
 * The point of this screen is to be actionable: every deduction is tied to a
 * timestamped event with a plain-language reason, so "your score is 72" always
 * comes with "because you braked at 4.2 m/s² three times".
 */
import { useCallback, useEffect, useState } from 'react';
import {
  EVENT_LABELS, JOURNEY_SCOPES, SCOPE_LABELS,
  type DrivingEvent, type JourneyScope, type JourneySummary, type ObdSession,
} from '@obd/core';
import type { CarRecordsApi } from '../store/records';
import type { StoredTrip } from '../store/db';
import { theme, useCanvasPainter, useLiveText } from './render';
import { FONT, drawSparkline, formatValue } from './draw';

const SCORE_KEYS = [
  { key: 'score_smoothness', weight: '35%' },
  { key: 'score_economy', weight: '30%' },
  { key: 'score_anticipation', weight: '20%' },
  { key: 'score_engine_care', weight: '15%' },
] as const;

/**
 * What the trip computer above does not already say: the figures that come
 * from somewhere else -- the fuel gauge, GPS, the pump.
 */
const TRIP_KEYS = ['range_km'] as const;

/** Shown only once they have something to report. */
const EXTRA_KEYS = ['trip_distance_gps', 'speed_sensor_error', 'fuel_correction'] as const;

export function TripView({
  session, connected, records,
}: {
  session: ObdSession;
  connected: boolean;
  records: CarRecordsApi;
}): JSX.Element {
  const [events, setEvents] = useState<readonly DrivingEvent[]>([]);

  // Events are appended rarely, so a slow poll is plenty.
  useEffect(() => {
    const id = setInterval(() => setEvents([...session.recentEvents].reverse()), 1000);
    return () => clearInterval(id);
  }, [session]);

  // Make sure the analysis inputs are actually being polled while this is open.
  useEffect(() => {
    if (!connected) return;
    session.setSubscriptions([
      ['rpm', 100], ['speed', 100], ['accelerator', 100], ['coolant_temp', 1000],
      ['maf', 250], ['engine_load', 250], ['fuel_level', 30000], ['score_overall', 500],
    ]);
  }, [session, connected]);

  const counts = new Map<string, number>();
  for (const e of events) counts.set(e.kind, (counts.get(e.kind) ?? 0) + 1);

  return (
    <div className="trip">
      <div className="trip-top">
        <ScoreCard session={session} />
        <TripComputer session={session} records={records} />
      </div>

      <div className="card">
        <h3>Range, GPS and calibration</h3>
        <div className="trip-grid">
          {TRIP_KEYS.map((key) => (
            <TripStat key={key} session={session} signalKey={key} />
          ))}
          {/* Re-checked every second, when the event list refreshes. */}
          {EXTRA_KEYS.filter((key) => session.store.get(key)?.available).map((key) => (
            <TripStat key={key} session={session} signalKey={key} />
          ))}
        </div>
      </div>

      <DriveLog records={records} />

      <div className="card">
        <h3>Score breakdown</h3>
        <div className="breakdown">
          {SCORE_KEYS.map(({ key, weight }) => (
            <BreakdownRow key={key} session={session} signalKey={key} weight={weight} />
          ))}
        </div>
      </div>

      <div className="card">
        <h3>What cost you points</h3>
        {events.length === 0 ? (
          <p className="muted">
            Nothing logged yet. Events appear here as they happen — hard acceleration, hard braking,
            abrupt throttle, over-revving, revving a cold engine, and long idling.
          </p>
        ) : (
          <>
            <div className="event-summary">
              {[...counts.entries()].map(([kind, n]) => (
                <span key={kind} className="pill">
                  {EVENT_LABELS[kind as DrivingEvent['kind']] ?? kind} × {n}
                </span>
              ))}
            </div>
            <div className="event-list">
              {events.slice(0, 60).map((e, i) => (
                <div key={e.t + '-' + i} className="event">
                  <span className="muted tnum event-time">
                    {new Date(e.t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                  <span
                    className="event-sev"
                    style={{ background: severityColor(e.severity) }}
                    aria-hidden
                  />
                  <span className="event-kind">{EVENT_LABELS[e.kind] ?? e.kind}</span>
                  <span className="muted event-detail">{e.detail}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The trip computer: the same figures a car shows, over the spans a car keeps.
 *
 * "Now" is the live reading, and the three spans are ratios of what has been
 * counted -- fuel over distance, distance over time -- rather than averages of
 * the instantaneous numbers, which is what makes them comparable with the
 * figure on the dashboard of the car itself.
 */
function TripComputer({ session, records }: { session: ObdSession; records: CarRecordsApi }): JSX.Element {
  const [, tick] = useState(0);
  const [confirmLife, setConfirmLife] = useState(false);

  // Once a second is plenty for averages that move this slowly.
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const summaries = JOURNEY_SCOPES.map((scope) => session.summary(scope));
  const cell = (row: ComputerRow, value: number | undefined): string =>
    value === undefined || !Number.isFinite(value) ? '—' : row.format ? row.format(value) : value.toFixed(row.decimals);
  const rows = COMPUTER_ROWS.map((row) => ({
    row,
    now: cell(row, row.now ? row.now(session) : undefined),
    values: summaries.map((summary) => cell(row, row.value(summary))),
  }));

  return (
    <div className="card trip-computer">
      <h3>Trip computer</h3>
      <div className="computer-table" role="table">
        <div className="computer-head" role="row">
          <span role="columnheader" />
          <span role="columnheader">Now</span>
          {JOURNEY_SCOPES.map((scope) => (
            <span key={scope} role="columnheader" title={SCOPE_LABELS[scope]}>{SCOPE_HEADS[scope]}</span>
          ))}
        </div>
        {rows.map(({ row, now, values }) => (
          <div className="computer-row" role="row" key={row.label}>
            <span className="computer-label" role="cell">
              {row.label}
              {row.unit && <span className="muted"> {row.unit}</span>}
            </span>
            <span className="tnum" role="cell">{now}</span>
            {values.map((value, i) => (
              <span className="tnum" role="cell" key={JOURNEY_SCOPES[i]}>{value}</span>
            ))}
          </div>
        ))}
      </div>

      <p className="muted small computer-note">
        Since fill-up restarts when you record a fill-up in Setup → Fuel. All time counts every drive of
        this car, and a drive is banked automatically once the engine has been off for a quarter of an hour.
      </p>

      <div className="button-row">
        <button className="btn" data-size="sm" onClick={() => void records.resetScope('trip')}>Reset trip</button>
        <button className="btn" data-size="sm" onClick={() => void records.resetScope('tank')}>Reset tank</button>
        <button className="btn" data-size="sm" onClick={() => setConfirmLife(true)}>Reset all time</button>
      </div>

      {confirmLife && (
        <div className="warn-card" role="alert" style={{ marginTop: 10 }}>
          Clear every kilometre, litre and hour recorded for this car? Fill-ups and the drive log are kept.
          <div className="button-row" style={{ marginTop: 8 }}>
            <button className="btn" data-size="sm" onClick={() => setConfirmLife(false)}>Cancel</button>
            <button
              className="btn"
              data-size="sm"
              data-variant="danger"
              onClick={() => {
                void records.resetScope('life');
                setConfirmLife(false);
              }}
            >
              Clear all-time totals
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const SCOPE_HEADS: Record<JourneyScope, string> = { trip: 'Trip', tank: 'Tank', life: 'All time' };

interface ComputerRow {
  label: string;
  unit?: string;
  decimals: number;
  /** The live reading, where one exists. */
  now?: (session: ObdSession) => number | undefined;
  value: (summary: JourneySummary) => number | undefined;
  /** For figures a number with a unit does not suit, e.g. time. */
  format?: (value: number) => string;
}

const COMPUTER_ROWS: ComputerRow[] = [
  { label: 'Distance', unit: 'km', decimals: 1, value: (s) => s.distanceKm },
  { label: 'Fuel used', unit: 'L', decimals: 2, value: (s) => s.fuelL },
  {
    label: 'Consumption', unit: 'L/100km', decimals: 1,
    now: (session) => session.store.value('consumption'),
    value: (s) => s.consumption,
  },
  {
    label: 'Speed', unit: 'km/h', decimals: 0,
    now: (session) => session.store.value('road_speed'),
    value: (s) => s.avgSpeedKph,
  },
  { label: 'Moving average', unit: 'km/h', decimals: 0, value: (s) => s.avgMovingKph },
  { label: 'Top speed', unit: 'km/h', decimals: 0, value: (s) => s.maxSpeedKph },
  { label: 'Engine running', decimals: 0, value: (s) => s.seconds, format: driveLength },
  { label: 'Idling', unit: '%', decimals: 0, value: (s) => s.idlePct },
  { label: 'Idle burn', unit: 'L/h', decimals: 2, value: (s) => s.idleLph },
  { label: 'Coasting', unit: '%', decimals: 0, value: (s) => s.coastPct },
  { label: 'Drives', decimals: 0, value: (s) => s.trips },
];

/** Drives this car has completed, newest first. */
function DriveLog({ records }: { records: CarRecordsApi }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const trips = expanded ? records.trips : records.trips.slice(0, 8);

  return (
    <div className="card">
      <h3>Drive log</h3>
      {records.trips.length === 0 ? (
        <p className="muted">
          No drives recorded yet. Each drive is logged by itself once the engine has been off for a
          quarter of an hour, or when you reset the trip.
        </p>
      ) : (
        <>
          <div className="drive-list">
            {trips.map((trip) => (
              <DriveRow key={trip.id} trip={trip} />
            ))}
          </div>
          <div className="button-row" style={{ marginTop: 10 }}>
            {records.trips.length > 8 && (
              <button className="btn" data-size="sm" onClick={() => setExpanded((v) => !v)}>
                {expanded ? 'Show fewer' : 'Show all ' + records.trips.length}
              </button>
            )}
            <button className="btn" data-size="sm" onClick={() => void records.clearTripLog()}>Clear log</button>
          </div>
        </>
      )}
    </div>
  );
}

function DriveRow({ trip }: { trip: StoredTrip }): JSX.Element {
  const when = new Date(trip.endedAt);
  return (
    <div className="drive-row">
      <span className="drive-when">
        {when.toLocaleDateString([], { day: 'numeric', month: 'short' })}
        <span className="muted"> {when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
      </span>
      <span className="tnum">{trip.distanceKm.toFixed(1)} km</span>
      <span className="tnum muted">{driveLength(trip.seconds)}</span>
      <span className="tnum">{trip.consumption === undefined ? '—' : trip.consumption.toFixed(1) + ' L/100km'}</span>
      <span className="tnum muted">{trip.avgSpeedKph === undefined ? '—' : trip.avgSpeedKph.toFixed(0) + ' km/h'}</span>
    </div>
  );
}

/** A drive's length, in the units a person would say it in. */
function driveLength(seconds: number): string {
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0) return h + 'h ' + String(m).padStart(2, '0') + 'm';
  return m + 'm ' + String(total % 60).padStart(2, '0') + 's';
}

function severityColor(sev: number): string {
  if (sev >= 0.66) return theme.get('--critical', '#d03b3b');
  if (sev >= 0.33) return theme.get('--serious', '#ec835a');
  return theme.get('--warning', '#fab219');
}

function ScoreCard({ session }: { session: ObdSession }): JSX.Element {
  const state = session.store.get('score_overall');

  const paint = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      if (!state) return;
      const value = state.value ?? 0;
      const cx = w / 2;
      const cy = h * 0.48;
      const r = Math.min(w, h) * 0.33;
      const arc = Math.max(8, r * 0.2);

      ctx.beginPath();
      ctx.arc(cx, cy, r, -Math.PI / 2, Math.PI * 1.5);
      ctx.strokeStyle = theme.get('--grid', '#2c2c2a');
      ctx.lineWidth = arc;
      ctx.stroke();

      const tone = value >= 80 ? '--good' : value >= 60 ? '--warning' : value >= 40 ? '--serious' : '--critical';
      ctx.beginPath();
      ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * value) / 100);
      ctx.strokeStyle = theme.get(tone, '#0ca30c');
      ctx.lineWidth = arc;
      ctx.lineCap = 'round';
      ctx.stroke();

      ctx.textAlign = 'center';
      ctx.fillStyle = theme.get('--ink-primary', '#fff');
      ctx.font = '600 ' + r * 0.7 + 'px ' + FONT;
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(value.toFixed(0), cx, cy + r * 0.24);

      ctx.font = '11px ' + FONT;
      ctx.fillStyle = theme.get('--ink-muted', '#898781');
      ctx.fillText(verdict(value), cx, cy + r + arc + 14);
    },
    [state],
  );
  const ref = useCanvasPainter(paint, 200);

  return (
    <div className="card score-card">
      <h3>Driving score</h3>
      <canvas ref={ref} className="score-canvas" />
      <p className="muted small">
        Penalties decay over about 90 seconds, so driving smoothly brings the score back up.
      </p>
    </div>
  );
}

function verdict(v: number): string {
  if (v >= 90) return 'Very smooth';
  if (v >= 75) return 'Smooth';
  if (v >= 60) return 'A bit brisk';
  if (v >= 40) return 'Aggressive';
  return 'Very aggressive';
}

function BreakdownRow({
  session, signalKey, weight,
}: {
  session: ObdSession;
  signalKey: string;
  weight: string;
}): JSX.Element | null {
  const state = session.store.get(signalKey);
  const valueRef = useLiveText<HTMLSpanElement>(() => (state?.value ?? 0).toFixed(0), 500);

  const paint = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      if (!state) return;
      const v = Math.max(0, Math.min(100, state.value ?? 0));
      const barH = Math.min(8, h);
      const y = (h - barH) / 2;
      ctx.fillStyle = theme.get('--grid', '#2c2c2a');
      ctx.beginPath();
      ctx.roundRect(0, y, w, barH, barH / 2);
      ctx.fill();
      const tone = v >= 80 ? '--good' : v >= 60 ? '--warning' : v >= 40 ? '--serious' : '--critical';
      ctx.fillStyle = theme.get(tone, '#0ca30c');
      ctx.beginPath();
      ctx.roundRect(0, y, Math.max((v / 100) * w, 3), barH, barH / 2);
      ctx.fill();
    },
    [state],
  );
  const barRef = useCanvasPainter(paint, 250);

  if (!state) return null;
  return (
    <div className="breakdown-row">
      <span className="breakdown-name">
        {state.def.name}
        <span className="muted"> · {weight}</span>
      </span>
      <canvas ref={barRef} className="breakdown-bar" />
      <span className="tnum breakdown-value" ref={valueRef}>0</span>
      <span className="muted breakdown-desc">{state.def.description}</span>
    </div>
  );
}

function TripStat({ session, signalKey }: { session: ObdSession; signalKey: string }): JSX.Element | null {
  const state = session.store.get(signalKey);
  const ref = useLiveText<HTMLSpanElement>(
    () => (state ? formatDuration(signalKey, state.value, state.def.decimals) : '--'),
    1000,
  );

  const paint = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      if (!state) return;
      drawSparkline(ctx, state.history, 0, 0, w, h, {
        points: 120,
        color: theme.get('--accent', '#3987e5'),
        fill: true,
      });
    },
    [state],
  );
  const sparkRef = useCanvasPainter(paint, 1000);

  if (!state) return null;
  return (
    <div className="trip-stat">
      <span className="muted trip-stat-label">{state.def.name}</span>
      <span className="trip-stat-value tnum">
        <span ref={ref}>--</span>
        <span className="muted trip-stat-unit"> {signalKey === 'trip_duration' ? '' : state.def.unit}</span>
      </span>
      <canvas ref={sparkRef} className="trip-stat-spark" />
    </div>
  );
}

/** Seconds read better as h:mm:ss than as a five-digit number. */
function formatDuration(key: string, value: number | undefined, decimals: number): string {
  if (value === undefined || !Number.isFinite(value)) return '--';
  // A correction reads as one only with its sign.
  if (key === 'speed_sensor_error') return (value > 0 ? '+' : '') + value.toFixed(decimals);
  if (key !== 'trip_duration') return value.toFixed(decimals);
  const total = Math.round(value);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return (h > 0 ? h + ':' + String(m).padStart(2, '0') : String(m)) + ':' + String(s).padStart(2, '0');
}

export { formatValue };
